export const getDollars = (amount = NaN) => {
  return amount.toFixed(2);
};

export const getCents = (amount = NaN) => {
  return +amount.toFixed(2).toString().split('.')[1];
};

export const getMetadataSampleColorSpecName = (attributes) => attributes?.find((attr) => attr.isSwatch)?.attributeName;

export const getSpecValue = ({ specificationGroup, specName }) => {
  const details = specificationGroup?.find((spec) => spec.specTitle === 'Details');
  return details?.specifications?.find((spec) => spec.specName === specName)?.specValue || '';
};

/**
 * Enhanced logic to find a distinguishing attribute value for a sample.
 * This function finds the first attribute that:
 * 1. Belongs to ALL sample specification groups (is common across all samples)
 * 2. Does NOT have the same specValue across all samples (provides differentiation)
 *
 * This solves the issue where all samples might have the same value for the first
 * matching attribute (e.g., "Interior Paint" for "Paint Type") but differ on
 * subsequent attributes (e.g., "Eggshell", "Matte" for "Sheen").
 */
export const getSampleNameAttribute = ({ specificationGroup, attributes, allSampleSpecificationGroups = [] }) => {
  const metadataColorSpecName = getMetadataSampleColorSpecName(attributes);
  if (metadataColorSpecName) {
    // If we have an isSwatch name, use it
    const isSwatchValue = getSpecValue({ specificationGroup, specName: metadataColorSpecName });
    if (isSwatchValue) {
      return { sampleAttrName: metadataColorSpecName, sampleAttrValue: isSwatchValue };
    }
  }

  // Only use new logic if we have all samples' specification groups
  if (allSampleSpecificationGroups.length === 0) {
    // If no samples provided, fallback to Color Family
    return {
      sampleAttrName: 'Color Family',
      sampleAttrValue: getSpecValue({ specificationGroup, specName: 'Color Family' })
    };
  }

  // Find all attribute names that exist in the anchor product
  const anchorAttributeNames = attributes.map((attr) => attr.attributeName);

  // For each anchor attribute, check if it's present in ALL samples and has varying values
  const differentiatingAttributeName = anchorAttributeNames.find((attributeName) => {
    // Check if this attribute exists in ALL sample specification groups
    const attributeExistsInAllSamples = allSampleSpecificationGroups.every((sampleSpecGroup) => {
      return sampleSpecGroup.some((specGroup) => specGroup.specifications
        && specGroup.specifications.some((spec) => spec.specName === attributeName));
    });

    if (!attributeExistsInAllSamples) {
      return false; // Skip attributes that don't exist in all samples
    }

    // Collect all spec values for this attribute across all samples
    const allSpecValues = allSampleSpecificationGroups.map((sampleSpecGroup) => {
      const foundSpecGroup = sampleSpecGroup.find((specGroup) => {
        return specGroup.specifications && specGroup.specifications.some((spec) => spec.specName === attributeName);
      });

      if (foundSpecGroup) {
        const spec = foundSpecGroup.specifications.find((specItem) => specItem.specName === attributeName);
        return spec ? spec.specValue : undefined;
      }
      return undefined;
    }).filter((value) => value !== undefined);

    // Check if this attribute has different values across samples
    const uniqueValues = [...new Set(allSpecValues)];
    const hasDifferentValues = uniqueValues.length > 1;
    
    // For single sample case, use the first matching attribute since differentiation doesn't apply
    const hasOneSample = allSampleSpecificationGroups.length === 1;

    return hasDifferentValues || hasOneSample;
  });

  if (differentiatingAttributeName) {
    return {
      sampleAttrName: differentiatingAttributeName,
      sampleAttrValue: getSpecValue({ specificationGroup, specName: differentiatingAttributeName })
    };
  }
  // fallback to Color Family if there is no proper combination name
  return {
    sampleAttrName: 'Color Family',
    sampleAttrValue: getSpecValue({ specificationGroup, specName: 'Color Family' })
  };
};

export const getSampleColorSpecValue = (attributes, specificationGroup) => {
  const metadataSpecName = getMetadataSampleColorSpecName(attributes);
  return getSpecValue({
    specificationGroup,
    specName: metadataSpecName
  });
};

export const getMetadataAttributeValues = (attributes) => {
  const metadataSpecName = getMetadataSampleColorSpecName(attributes);
  return attributes.find((attr) => attr.attributeName === metadataSpecName)
    ?.attributeValues?.map((attrValue) => attrValue.value) || [];
};

export const normalizeImageUrl = (url, size) => {
  const urlRE = /<SIZE>/i;
  return url.replace(urlRE, size);
};

const getMatchingChildItemId = ({ childItems, specificSkuName, excludedItemIds }) => {
  return childItems
    .find((childItem) => {
      return !!childItem.itemId
        && !excludedItemIds.includes(childItem.itemId)
        && childItem.attributeCombination.split('~~').includes(specificSkuName);
    })?.itemId;
};
const getFilteredChildItemIds = ({ childItems, specificSkuNames, excludedItemIds }) => {
  return childItems
    .filter((childItem) => {
      if (!!childItem.itemId && !excludedItemIds.includes(childItem.itemId)) {
        return specificSkuNames.filter((specificSkuName) => {
          return childItem.attributeCombination.split('~~').includes(specificSkuName);
        }).length;
      }
      return false;
    })
    .map((childItem) => childItem.itemId);
};

export const getAnchorItemIds = ({ metadata, specificSkuNames = [], excludedItemIds = [] }) => {
  if (metadata) {
    const childItems = metadata.childItemsLookup || [];
    const attributes = metadata.attributes || [];
    const skuNames = getMetadataAttributeValues(attributes)
      .filter((value) => !specificSkuNames.length || specificSkuNames.includes(value));

    if (childItems.length) {
      if (skuNames.length) {
        const tempItemIds = [];
        skuNames.forEach((skuName) => {
          // eslint-disable-next-line max-len
          const childItemsWithSkuName = getMatchingChildItemId({ childItems, specificSkuName: skuName, excludedItemIds });
          if (childItemsWithSkuName) {
            tempItemIds.push(childItemsWithSkuName);
          }
        });
        return tempItemIds;
      }
      return getFilteredChildItemIds({ childItems, specificSkuNames, excludedItemIds });
    }
  }
  return [];
};

export const getUncalledUniqueSampleIdsFromProducts = (products) => {
  return [...new Set(products.map((product) => {
    return product?.identifiers?.sampleId;
  }).filter((sampleId) => {
    return !!sampleId;
  }))];
};